import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSDestructuring = () => {
    const basicPatternsCode = `// 1. Array Destructuring (Positional)
const settings = [1920, 1080, 'dark'];
const [width, height, theme] = settings;

// 2. Object Destructuring (Property-based)
const user = { id: 101, alias: 'Neo', role: 'Admin' };
const { alias, role } = user; // Order doesn't matter

// 3. Renaming during extraction
const { alias: handle } = user;
console.log(handle); // 'Neo'`;

    const advancedPipelinesCode = `// --- Deep Data Pipeline Extraction ---
const apiResponse = {
    status: 200,
    payload: {
        meta: { requestId: 'v4-892' },
        records: [
            { id: 1, val: 'Alpha' },
            { id: 2, val: 'Beta' }
        ]
    }
};

// Extracting nested values in one clean motion
const { 
    payload: { 
        meta: { requestId },
        records: [firstRecord]
    } 
} = apiResponse;

console.log(requestId);   // 'v4-892'
console.log(firstRecord); // { id: 1, val: 'Alpha' }`;

    const dataScrubbingCode = `// --- The "Rest" Pattern for Data Scrubbing ---
const incomingUser = {
    id: 50,
    email: 'user@example.com',
    password: 'HASHED_SECRET_DO_NOT_EXPOSE',
    lastLogin: '2024-01-01'
};

// Scrub sensitive data while extracting the rest
const { password, ...publicProfile } = incomingUser;

console.log(publicProfile); 
// { id: 50, email: 'user@example.com', lastLogin: '2024-01-01' }
// password is NOT in publicProfile.`;

    const parameterObjectCode = `// --- The "Parameter Object" Pattern ---
// Prevents "Argument List Hell" in large functions

function configureServer({
    port = 8080,
    secure = true,
    cacheLimit = '2GB'
} = {}) {
    console.log(\`Running on port \${port}, Security: \${secure}\`);
}

// Caller can specify only what's needed, in any order
configureServer({ port: 3000 });`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Advanced Patterns</div>
                <h1>Elegant Data Extraction</h1>
                <p className="hero-lead">
                    Destructuring is more than just syntactic sugar. It is a powerful
                    mechanism for extracting, renaming, and scrubbing data from
                    complex structures with precision and minimal boilerplate.
                </p>
            </div>

            <div className="content-section">
                <h2>Patterns and Positional Extraction</h2>
                <p>
                    JavaScript supports two primary forms of destructuring: <strong>Position-based</strong>
                    for arrays and <strong>Name-based</strong> for objects. While
                    array destructuring relies on order, object destructuring allows
                    you to extract properties by key and even rename them locally to
                    avoid naming conflicts.
                </p>
                <CodeBlock language="javascript" code={basicPatternsCode} />
            </div>

            <div className="content-section">
                <h2>Deep Extraction Pipelines</h2>
                <p>
                    For complex JSON payloads from APIs, nested destructuring allows
                    you to dig into several layers of data in a single line. This
                    approach is declarativeit describes the <em>shape</em> of the
                    data you want to extract rather than the manual assignment steps.
                </p>
                <CodeBlock language="javascript" code={advancedPipelinesCode} />
            </div>

            <div className="content-section">
                <h2>Data Scrubbing and Immutability</h2>
                <p>
                    The <strong>Rest Pattern</strong> (<code>...</code>) is
                    indispensable for data scrubbing. By destructuring sensitive
                    keys (like passwords or internal IDs) and gathering the rest,
                    you create a clean, shallow copy of your data for safe
                    transmission or state management.
                </p>
                <CodeBlock language="javascript" code={dataScrubbingCode} />
            </div>

            <div className="content-section">
                <h2>The Parameter Object Pattern</h2>
                <p>
                    As functions grow, a long list of positional arguments
                    (e.g., <code>fn(a, b, c, d, e)</code>) becomes unmanageable.
                    Destructuring enables the "Parameter Object" pattern, where
                    you pass a single object. This makes your API much more flexible
                    and allows for clear default values.
                </p>
                <CodeBlock language="javascript" code={parameterObjectCode} />
                <ProTip><strong> Pro Tip:</strong> Always set a default empty object
                    <code>= {"{}"}</code> for your parameter object to prevent
                    errors if the function is called without any arguments.</ProTip>
            </div>

            <KeyFacts><ul>
                    <li><strong>Renaming:</strong> Use <code>variable: alias</code> to avoid collisions with local scopes.</li>
                    <li><strong>Defaults:</strong> Provide fallback values for optional API fields.</li>
                    <li><strong>Scrubbing:</strong> Use the rest operator to remove sensitive fields before sending objects to the UI.</li>
                    <li> <strong>Assignment:</strong> Be careful with <code>null</code>destructuring defaults only kick in for <code>undefined</code>.</li>
                    <li><strong>Consistency:</strong> Use destructuring in <code>for...of</code> loops for cleaner iteration over complex collections.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSDestructuring;


